/**
 * Tokens en TS para consumidores que no son Tailwind: Recharts pinta con
 * atributos SVG (`stroke`, `fill`), no con clases.
 *
 * NO son una paleta paralela: se leen del mismo `tailwind-preset.cjs` que define
 * las clases y las CSS vars. Si un color cambia, cambia en un solo archivo.
 *
 * El preset es **CJS y vive fuera de node_modules**, así que la interoperabilidad
 * depende del bundler. Por eso se importa con `import * as` y no con un `import
 * default`: si algún día el pre-bundle deja de convertirlo, esto degrada a
 * valores vacíos en vez de tirar un `SyntaxError` que se lleva puesta la app
 * entera. El test de `tokens` verifica los valores reales, así que una
 * degradación así se cae en CI y no en producción.
 *
 * Ver `optimizeDeps.include` en `vite.config.ts`.
 */
import * as presetModule from '@ops/contracts/tailwind-preset'
import type { Health } from '@ops/contracts'

const preset = presetModule.default ?? presetModule

export const COLOR: Record<string, string> = preset.LABUREN_COLORS ?? {}
export const CHART: Record<string, string> = preset.LABUREN_CHART_COLORS ?? {}
export const SHADOW: Record<string, string> = preset.LABUREN_SHADOWS ?? {}

/**
 * Vocabulario de severidad de todo el panel. Es el `Health` del contrato y no un
 * enum nuevo: un segundo vocabulario de "está roto" garantiza que en algún lugar
 * los dos no coincidan.
 */
export type Severidad = Health

/** Texto obligatorio de cada severidad. El color nunca viaja solo. */
export const SEVERIDAD_LABEL: Record<Severidad, string> = {
  ok: 'En target',
  warning: 'Atención',
  critical: 'Crítico',
  unknown: 'Sin dato',
}

/** Relleno: punto, barra, borde. Los de marca no se usan como letra. */
export const SEVERIDAD_RELLENO: Record<Severidad, string> = {
  ok: 'bg-accent',
  warning: 'bg-warning',
  critical: 'bg-danger',
  unknown: 'bg-n300',
}

/** Color de letra legible de cada severidad (todos ≥ 4,5:1 sobre blanco). */
export const SEVERIDAD_TEXTO: Record<Severidad, string> = {
  ok: 'text-accent-text',
  warning: 'text-warning-text',
  critical: 'text-danger-text',
  unknown: 'text-muted-strong',
}

/** Fondo tintado + borde para pills y bloques. */
export const SEVERIDAD_TONO: Record<Severidad, string> = {
  ok: 'bg-accent-soft border-accent',
  warning: 'bg-warning-soft border-warning',
  critical: 'bg-danger-soft border-danger',
  unknown: 'bg-n100 border-border',
}

/** Borde izquierdo de 3px: la señal de severidad de tarjetas y filas. */
export const SEVERIDAD_BORDE: Record<Severidad, string> = {
  ok: 'border-l-accent',
  warning: 'border-l-warning',
  critical: 'border-l-danger',
  unknown: 'border-l-n300',
}

/** Trazo del sparkline y de las series de Recharts, según el semáforo. */
export const SEVERIDAD_TRAZO: Record<Severidad, string | undefined> = {
  ok: CHART.serie,
  warning: CHART.atencion,
  critical: CHART.perdida,
  unknown: CHART.neutro,
}
